import { ComponentType, FC } from 'react';
import { UseQueryResult } from 'react-query';
import { useNavigate } from 'react-router-dom';
import useLogout from '../hooks/useLogout.ts';
import Backdrop from '../ui/Backdrop.tsx';
import Spinner from '../ui/Spinner.tsx';

import H3 from '../ui/typography/H3.tsx';
import PrimaryButton from '../ui/buttons/PrimaryButton.tsx';
import { IMeData } from '../api/services/user/types.ts';
import AuthLayout from '../ui/layout/AuthLayout';
import { useGlobalStore } from '../providers/GlobalStoreProvider.tsx';
import Routes from '../router/routes.enum.ts';

// Base props that will be injected by the HOC
export type MePropsType = Pick<UseQueryResult<any>, 'isError' | 'isLoading'> & {
  user: IMeData;
};

// Generic type for component props
const withMe = <P extends object>(Component: ComponentType<P & MePropsType>): FC<P> => {
  const WithMeComponent: FC<P> = (props) => {
    const {
      user: { data: user, isLoading, isError },
    } = useGlobalStore();
    const navigate = useNavigate();
    const [logout, logoutState] = useLogout(true);

    const handleLogout = () => {
      navigate(Routes.SignIn);
      logout();
    };

    if (isLoading || logoutState.isLoading) {
      return (
        <Backdrop>
          <Spinner />
        </Backdrop>
      );
    }

    if (isError || !user) {
      return (
        <AuthLayout>
          <H3>Something went wrong, please try again.</H3>

          <PrimaryButton onClick={handleLogout} className="mt-[32px]">
            Go to Sign In
          </PrimaryButton>
        </AuthLayout>
      );
    }

    if (!user) return null;

    return <Component user={user} isLoading={isLoading} isError={isError} {...props} />;
  };

  // Add display name for better debugging
  WithMeComponent.displayName = `WithMe(${Component.displayName || Component.name || 'Component'})`;

  return WithMeComponent;
};

export default withMe;
